import type {
  ChartType,
  CubeQueryConfig,
  GUIBuilderFilter,
} from "@web-app/types";

// ============================================================================
// Type Definitions
// ============================================================================

export type MetricCategory = "distance" | "operations" | "performance";

export type DimensionCategory =
  "category" | "custom" | "location" | "people" | "time";

export interface MetricOption {
  category: MetricCategory;
  cubeMapping: {
    cube: string;
    measure: string;
    type: "avg" | "count" | "max" | "min" | "sum";
  };
  description?: string;
  id: string;
  label: string;
  suggestedChartTypes: ChartType[];
}

export interface DimensionOption {
  category: DimensionCategory;
  cubeMapping: {
    cube: string;
    dimension: string;
    granularity?: string;
  };
  description?: string;
  id: string;
  label: string;
  suggestedChartTypes: ChartType[];
}

// ============================================================================
// Metric Definitions
// ============================================================================

export const GUIDED_METRICS: MetricOption[] = [
  // ============================================================================
  // Operations Metrics (What's happening now)
  // ============================================================================
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.count",
      type: "count",
    },
    description: "Total number of orders",
    id: "total-orders",
    label: "Total Orders",
    suggestedChartTypes: ["metric", "line", "bar", "column"],
  },
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.total_items",
      type: "sum",
    },
    description: "Total number of items across all orders",
    id: "total-items",
    label: "Total Items",
    suggestedChartTypes: ["metric", "line", "area"],
  },
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.routed_orders_count",
      type: "count",
    },
    description: "Number of orders assigned to routes",
    id: "routed-orders",
    label: "Routed Orders",
    suggestedChartTypes: ["metric", "bar"],
  },
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.unrouted_orders_count",
      type: "count",
    },
    description: "Number of orders not assigned to routes",
    id: "unrouted-orders",
    label: "Unrouted Orders",
    suggestedChartTypes: ["metric", "bar"],
  },
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.avg_items_per_order",
      type: "avg",
    },
    description: "Average number of items per order",
    id: "avg-items-per-order",
    label: "Avg Items per Order",
    suggestedChartTypes: ["metric", "line"],
  },

  // ============================================================================
  // Performance & Quality Metrics (How well are we doing)
  // ============================================================================
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.completion_rate",
      type: "avg",
    },
    description: "Percentage of completed orders",
    id: "completion-rate",
    label: "Completion Rate (%)",
    suggestedChartTypes: ["metric", "line", "gauge"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.pickup_otif_rate",
      type: "avg",
    },
    description: "Percentage of orders with on-time pickup",
    id: "pickup-otif",
    label: "Pickup OTIF (%)",
    suggestedChartTypes: ["gauge", "metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.pickup_late_rate",
      type: "avg",
    },
    description: "Percentage of orders with late pickup",
    id: "pickup-late-rate",
    label: "Pickup Late (%)",
    suggestedChartTypes: ["gauge", "metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.pickup_on_time_count",
      type: "sum",
    },
    description: "Count of orders with on-time pickup",
    id: "pickup-on-time-count",
    label: "Pickup On-Time Count",
    suggestedChartTypes: ["metric", "bar", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.pickup_late_count",
      type: "sum",
    },
    description: "Count of orders with late pickup",
    id: "pickup-late-count",
    label: "Pickup Late Count",
    suggestedChartTypes: ["metric", "bar", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.dropoff_otif_rate",
      type: "avg",
    },
    description: "Percentage of orders with on-time dropoff",
    id: "dropoff-otif",
    label: "Dropoff OTIF (%)",
    suggestedChartTypes: ["gauge", "metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.dropoff_late_rate",
      type: "avg",
    },
    description: "Percentage of orders with late dropoff",
    id: "dropoff-late-rate",
    label: "Dropoff Late (%)",
    suggestedChartTypes: ["gauge", "metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.dropoff_on_time_count",
      type: "sum",
    },
    description: "Count of orders with on-time dropoff",
    id: "dropoff-on-time-count",
    label: "Dropoff On-Time Count",
    suggestedChartTypes: ["metric", "bar", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.dropoff_late_count",
      type: "sum",
    },
    description: "Count of orders with late dropoff",
    id: "dropoff-late-count",
    label: "Dropoff Late Count",
    suggestedChartTypes: ["metric", "bar", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.avg_pickup_to_dropoff_duration",
      type: "avg",
    },
    description: "Average duration from pickup to dropoff (seconds)",
    id: "avg-pickup-to-dropoff",
    label: "Avg Pickup to Dropoff Time",
    suggestedChartTypes: ["metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.avg_total_order_duration",
      type: "avg",
    },
    description: "Average total order duration (seconds)",
    id: "avg-order-duration",
    label: "Avg Order Duration",
    suggestedChartTypes: ["metric", "line"],
  },
  {
    category: "performance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.avg_service_time",
      type: "avg",
    },
    description: "Average service time per order (seconds)",
    id: "avg-service-time",
    label: "Avg Service Time",
    suggestedChartTypes: ["metric", "line"],
  },

  // ============================================================================
  // Distance & Logistics Metrics (Resources used)
  // ============================================================================
  {
    category: "distance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.total_distance_covered",
      type: "sum",
    },
    description: "Total distance covered in miles",
    id: "total-distance",
    label: "Total Distance Covered",
    suggestedChartTypes: ["metric", "line", "area"],
  },
  {
    category: "distance",
    cubeMapping: {
      cube: "orders",
      measure: "orders.avg_distance_covered",
      type: "avg",
    },
    description: "Average distance covered per order (miles)",
    id: "avg-distance",
    label: "Avg Distance per Order",
    suggestedChartTypes: ["metric", "line"],
  },

  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.signature_required_count",
      type: "count",
    },
    description: "Orders requiring signature",
    id: "signature-required",
    label: "Signature Required Orders",
    suggestedChartTypes: ["metric", "bar"],
  },
  {
    category: "operations",
    cubeMapping: {
      cube: "orders",
      measure: "orders.proof_of_delivery_required_count",
      type: "count",
    },
    description: "Orders requiring proof of delivery",
    id: "pod-required",
    label: "POD Required Orders",
    suggestedChartTypes: ["metric", "bar"],
  },
];

// ============================================================================
// Dimension Definitions
// ============================================================================

export const GUIDED_DIMENSIONS: DimensionOption[] = [
  // Time Dimensions
  {
    category: "time",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.created_at",
      granularity: "hour",
    },
    description: "See patterns throughout the day",
    id: "by-hour",
    label: "Hour of Day",
    suggestedChartTypes: ["line", "column", "area"],
  },
  {
    category: "time",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.created_at",
      granularity: "day",
    },
    description: "View daily trends",
    id: "by-day",
    label: "Day",
    suggestedChartTypes: ["line", "column", "area"],
  },
  {
    category: "time",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.created_at",
      granularity: "week",
    },
    description: "View weekly trends",
    id: "by-week",
    label: "Week",
    suggestedChartTypes: ["line", "column", "area"],
  },
  {
    category: "time",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.created_at",
      granularity: "month",
    },
    description: "View monthly trends",
    id: "by-month",
    label: "Month",
    suggestedChartTypes: ["line", "column", "area"],
  },

  // Location Dimensions
  {
    category: "location",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.state",
    },
    description: "Compare by region",
    id: "by-state",
    label: "Region",
    suggestedChartTypes: ["bar", "table", "pie", "donut"],
  },

  // People Dimensions
  {
    category: "people",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.assignee",
    },
    description: "Compare performance by assigned driver",
    id: "by-assignee",
    label: "Driver/Assignee",
    suggestedChartTypes: ["bar", "table"],
  },
  {
    category: "people",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.workspace_name",
    },
    description: "Compare by workspace",
    id: "by-workspace",
    label: "Workspace",
    suggestedChartTypes: ["bar", "pie"],
  },

  // Category Dimensions
  {
    category: "category",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.status",
    },
    description: "Break down by order status",
    id: "by-status",
    label: "Order Status",
    suggestedChartTypes: ["pie", "donut", "bar"],
  },
  {
    category: "category",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.delivery_type",
    },
    description: "Compare by delivery type",
    id: "by-delivery-type",
    label: "Delivery Type",
    suggestedChartTypes: ["pie", "donut", "bar"],
  },
  {
    category: "category",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.priority",
    },
    description: "Group by priority level",
    id: "by-priority",
    label: "Priority Level",
    suggestedChartTypes: ["pie", "bar"],
  },
  {
    category: "category",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.vehicle_type",
    },
    description: "Group by vehicle type",
    id: "by-vehicle-type",
    label: "Vehicle Type",
    suggestedChartTypes: ["pie", "bar"],
  },
  {
    category: "category",
    cubeMapping: {
      cube: "orders",
      dimension: "orders.route_name",
    },
    description: "Compare by route",
    id: "by-route",
    label: "Route",
    suggestedChartTypes: ["bar", "table"],
  },
];

// ============================================================================
// Filter Field Definitions
// ============================================================================

export interface FilterFieldOption {
  cubeMapping: {
    cube: string;
    dimension: string;
  };
  id: string;
  label: string;
  operators: Array<{
    label: string;
    value: string;
  }>;
  values: Array<{
    label: string;
    value: string;
  }>;
}

export const GUIDED_FILTER_FIELDS: FilterFieldOption[] = [
  {
    cubeMapping: {
      cube: "orders",
      dimension: "orders.status",
    },
    id: "status",
    label: "Order Status",
    operators: [
      { label: "is", value: "equals" },
      { label: "is not", value: "notEquals" },
      { label: "is one of", value: "equals" },
    ],
    values: [
      { label: "Complete", value: "Complete" },
      { label: "In Progress", value: "In Progress" },
      { label: "Failed", value: "Failed" },
      { label: "Cancelled", value: "Cancelled" },
      { label: "To Do", value: "To Do" },
    ],
  },
  {
    cubeMapping: {
      cube: "orders",
      dimension: "orders.delivery_type",
    },
    id: "delivery-type",
    label: "Delivery Type",
    operators: [
      { label: "is", value: "equals" },
      { label: "is not", value: "notEquals" },
      { label: "is one of", value: "equals" },
    ],
    values: [
      { label: "Pickup", value: "Pickup" },
      { label: "Dropoff", value: "Dropoff" },
      { label: "Point to Point", value: "Pickup and Dropoff" },
    ],
  },
  {
    cubeMapping: {
      cube: "orders",
      dimension: "orders.state",
    },
    id: "state",
    label: "Region",
    operators: [
      { label: "is", value: "equals" },
      { label: "is not", value: "notEquals" },
    ],
    values: [], // Dynamic - populated from API via useOrderMarkets
  },
];

// ============================================================================
// Helper Functions
// ============================================================================

export const METRIC_CATEGORIES: Record<
  MetricCategory,
  { icon: string; label: string }
> = {
  distance: { icon: "fasRoute", label: "Distance & Logistics" },
  operations: { icon: "fasClipboardList", label: "Operations" },
  performance: { icon: "fasClock", label: "Performance & Quality" },
};

export const DIMENSION_CATEGORIES: Record<
  Exclude<DimensionCategory, "custom">,
  { icon: string; label: string }
> = {
  category: { icon: "fasTag", label: "Category Breakdown" },
  location: { icon: "fasMapMarkerAlt", label: "Location Breakdown" },
  people: { icon: "fasUsers", label: "People Breakdown" },
  time: { icon: "fasCalendar", label: "Time Breakdown" },
};

/**
 * Get metrics grouped by category
 */
export function getGroupedMetrics(): Record<MetricCategory, MetricOption[]> {
  const grouped = {} as Record<MetricCategory, MetricOption[]>;

  GUIDED_METRICS.forEach((metric) => {
    if (!grouped[metric.category]) {
      grouped[metric.category] = [];
    }
    grouped[metric.category].push(metric);
  });

  return grouped;
}

/**
 * Get dimensions grouped by category
 */
export function getGroupedDimensions(): Record<
  DimensionCategory,
  DimensionOption[]
> {
  const grouped = {} as Record<DimensionCategory, DimensionOption[]>;

  GUIDED_DIMENSIONS.forEach((dimension) => {
    if (!grouped[dimension.category]) {
      grouped[dimension.category] = [];
    }
    grouped[dimension.category].push(dimension);
  });

  return grouped;
}

/**
 * Generate chart title from selections
 */
export function generateChartTitle(
  metric: MetricOption | null,
  dimension: DimensionOption | null,
): string {
  if (!metric) return "";

  if (!dimension) {
    return metric.label;
  }

  return `${metric.label} ${dimension.label}`;
}

/**
 * Suggest chart types based on metric and dimension
 */
export function suggestChartTypes(
  _metric: MetricOption | null,
  dimension: DimensionOption | null,
): ChartType[] {
  // Single value, no dimension
  if (!dimension) {
    return ["metric", "gauge"];
  }

  // Time-based dimension
  if (dimension.category === "time") {
    return ["line", "area", "column", "bar"];
  }

  // Categorical dimension with few items expected
  if (["location", "category"].includes(dimension.category)) {
    return ["bar", "column", "pie", "donut"];
  }

  // Multiple items expected (drivers, customers)
  if (dimension.category === "people") {
    return ["bar", "column", "table"];
  }

  // Default fallback
  return ["bar", "column", "line", "table"];
}

/**
 * Build Cube query from guided selections
 */
export function buildCubeQueryFromGuided(
  metric: MetricOption,
  dimension: DimensionOption | null,
  dateRange: { from: string; to: string },
  filters: GUIBuilderFilter[] = [],
  limit: number = 100,
): CubeQueryConfig {
  const query: CubeQueryConfig = {
    dimensions: [],
    limit,
    measures: [metric.cubeMapping.measure],
  };

  if (dimension) {
    if (dimension.category === "time") {
      // Time dimension - use it for grouping AND filtering
      query.timeDimensions = [
        {
          dateRange: [dateRange.from, dateRange.to],
          dimension: dimension.cubeMapping.dimension,
          granularity: dimension.cubeMapping.granularity,
        },
      ];
    } else {
      // Regular dimension - add it to dimensions and add time filter separately
      query.dimensions = [dimension.cubeMapping.dimension];

      // Sort by measure descending so highest values appear first
      query.order = { [metric.cubeMapping.measure]: "desc" };

      // Add date range filter using scheduled_start_time
      // This ensures the dashboard date range is always applied
      query.timeDimensions = [
        {
          dateRange: [dateRange.from, dateRange.to],
          dimension: "orders.scheduled_start_time",
        },
      ];
    }
  } else {
    // No dimension selected (metric only) - still apply date range filter
    query.timeDimensions = [
      {
        dateRange: [dateRange.from, dateRange.to],
        dimension: "orders.scheduled_start_time",
      },
    ];
  }

  // Add filters if any
  if (filters.length > 0) {
    query.filters = filters
      .filter((f) => f.values.length > 0) // Only include filters with values
      .map((filter) => {
        const fieldConfig = GUIDED_FILTER_FIELDS.find(
          (f) => f.id === filter.field,
        );
        if (!fieldConfig) {
          throw new Error(`Unknown filter field: ${filter.field}`);
        }

        return {
          member: fieldConfig.cubeMapping.dimension,
          operator: filter.operator,
          values: filter.values,
        };
      });
  }

  return query;
}
